import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import StockManagerLayout from '../components/StockManagerLayout';
import { API_BASE_URL } from '../utils/apiConfig';

const ROLES = ['CASHIER', 'ONLINE_CASHIER', 'HYBRID_CASHIER', 'SUPERVISOR', 'STOCKMANAGER', 'INVENTORY', 'STOCK_MANAGER', 'ADMIN', 'PROPRIETOR', 'DIRECTOR', 'ONLINE_ORDER_MANAGER', 'PACKING_AGENT', 'DISPATCH_AGENT', 'DELIVERY_AGENT', 'HYBRID_AGENT'];
const LOCATIONS = ['YANAM', 'MURAMULLA', 'GOLLAVELLI', 'GOLLAVELLI_OLD', 'VADAPARRU', 'UPPALAGUPTHAM', 'AMALAPURAM', 'RAVULACHERUVU'];
const asLocations = value => [...new Set((Array.isArray(value) ? value : [value]).filter(Boolean).map(item => String(item).trim().toUpperCase()).filter(Boolean))];
const emptyForm = { username: '', roles: ['CASHIER'], location: [], isActive: true, password: '', confirmPassword: '' };
const inputClass = 'mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2';
const buttonClass = 'rounded-lg border px-3 py-2 text-sm font-semibold disabled:opacity-50';

export default function POSUserManagementPage() {
  const currentUser = useSelector((state) => state.posUser?.userInfo);
  const [users, setUsers] = useState([]);
  const [locations, setLocations] = useState(LOCATIONS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('all');
  const [editor, setEditor] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);

  const request = useCallback(async (path = '', options = {}) => {
    const response = await fetch(`${API_BASE_URL}/posusers${path}`, {
      ...options,
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${currentUser?.token}` },
    });
    const data = await response.json().catch(() => null);
    if (!response.ok) throw new Error(data?.message || 'Unable to complete the request. Please try again.');
    return data;
  }, [currentUser?.token]);

  const loadUsers = useCallback(async (signal) => {
    setLoading(true);
    setError('');
    try {
      const data = await request('', { signal, cache: 'no-store' });
      if (!Array.isArray(data)) throw new Error('Unable to read the user list. Please try again.');
      // Only retain fields needed by this screen; never keep returned password hashes.
      setUsers(data.map(({ _id, username, role, roles, location, isActive }) => ({ _id, username, roles: asLocations(roles?.length ? roles : role), location: asLocations(location), isActive })));
    } catch (err) {
      if (err.name !== 'AbortError') setError(err.message);
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, [request]);

  useEffect(() => {
    const controller = new AbortController();
    loadUsers(controller.signal);
    request('/login-locations', { signal: controller.signal }).then((data) => {
      if (Array.isArray(data?.locations)) setLocations([...new Set([...LOCATIONS, ...data.locations])]);
    }).catch(() => {});
    return () => controller.abort();
  }, [loadUsers, request]);

  const openEditor = (mode, user) => {
    setEditor({ mode, user });
    setForm(user ? { ...emptyForm, username: user.username, roles: user.roles, location: asLocations(user.location), isActive: user.isActive !== false } : { ...emptyForm });
    setFormError('');
    setMessage('');
  };
  const change = (key, value) => setForm((previous) => ({ ...previous, [key]: value }));
  const save = async (event) => {
    event.preventDefault();
    if (saving) return;
    const passwordRequired = editor.mode !== 'edit';
    if (passwordRequired && (form.password.length < 8 || form.password !== form.confirmPassword)) {
      setFormError('Use at least 8 characters and make sure both passwords match.');
      return;
    }
    if (editor.mode !== 'reset' && !form.roles.length) { setFormError('Select at least one role.'); return; }
    if (editor.mode !== 'reset' && (!form.username.trim() || (!form.roles.includes('DIRECTOR') && !form.location.length))) {
      setFormError('Enter a username and select at least one location (optional for directors).');
      return;
    }
    const payload = editor.mode === 'reset' ? { password: form.password } : {
      username: form.username.trim(), role: form.roles[0], roles: form.roles, location: form.location, isActive: form.isActive,
      ...(editor.mode === 'create' ? { password: form.password } : {}),
    };
    setSaving(true);
    setFormError('');
    try {
      await request(editor.mode === 'create' ? '' : `/${encodeURIComponent(editor.user._id)}`, {
        method: editor.mode === 'create' ? 'POST' : 'PUT', body: JSON.stringify(payload),
      });
      setMessage(editor.mode === 'reset' ? `Password reset for ${editor.user.username}.` : editor.mode === 'create' ? 'POS user created.' : 'POS user updated.');
      setEditor(null);
      setForm({ ...emptyForm });
      await loadUsers();
    } catch (err) {
      setFormError(err.message);
    } finally {
      setSaving(false);
    }
  };
  const filtered = users.filter((user) =>
    [user.username, user.roles, user.location].some((value) => String(value || '').toLowerCase().includes(search.trim().toLowerCase())) &&
    (status === 'all' || (status === 'active' ? user.isActive !== false : user.isActive === false))
  );
  const ownAccount = editor?.user?._id === currentUser?._id;

  return (
    <StockManagerLayout>
      <div className="space-y-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div><h1 className="text-2xl font-bold text-gray-900">POS User Management</h1><p className="mt-1 text-sm text-gray-500">Create accounts, manage access, and reset passwords.</p></div>
          <button className={`${buttonClass} bg-blue-700 text-white`} onClick={() => openEditor('create')}>Create user</button>
        </div>
        {message && <div role="status" className="rounded-lg bg-green-50 p-3 text-green-800">{message}</div>}
        {error && <div role="alert" className="rounded-lg bg-red-50 p-3 text-red-700">{error} <button className="underline" onClick={() => loadUsers()}>Retry</button></div>}
        <div className="rounded-xl border bg-white p-4 shadow-sm">
          <div className="mb-4 flex flex-wrap items-end gap-3">
            <label className="min-w-48 flex-1 text-sm font-medium">Search users<input className={inputClass} value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Username, role or location" /></label>
            <label className="text-sm font-medium">Status<select className={inputClass} value={status} onChange={(event) => setStatus(event.target.value)}><option value="all">All users</option><option value="active">Active</option><option value="inactive">Inactive</option></select></label>
            <button className={buttonClass} disabled={loading || saving} onClick={() => loadUsers()}>Refresh</button>
          </div>
          {loading ? <p role="status" className="py-8 text-center text-gray-500">Loading users…</p> : error ? null : <>
            <p className="mb-3 text-sm text-gray-500">{filtered.length} of {users.length} users</p>
            <div className="overflow-x-auto"><table className="w-full text-left text-sm">
              <thead className="bg-gray-50 text-gray-600"><tr>{['Username', 'Roles', 'Locations', 'Status', 'Actions'].map((heading) => <th key={heading} className="px-3 py-3">{heading}</th>)}</tr></thead>
              <tbody>{filtered.map((user) => <tr key={user._id} className="border-t">
                <td className="px-3 py-3 font-semibold">{user.username}{user._id === currentUser?._id && <span className="ml-2 text-xs text-gray-500">(you)</span>}</td>
                <td className="px-3 py-3">{user.roles.join(', ')}</td><td className="px-3 py-3">{asLocations(user.location).join(', ') || '—'}</td>
                <td className="px-3 py-3"><span className={`rounded-full px-2 py-1 text-xs ${user.isActive === false ? 'bg-gray-100 text-gray-600' : 'bg-green-50 text-green-700'}`}>{user.isActive === false ? 'Inactive' : 'Active'}</span></td>
                <td className="px-3 py-3"><div className="flex gap-2 whitespace-nowrap"><button className={buttonClass} onClick={() => openEditor('edit', user)} aria-label={`Edit ${user.username}`}>Edit</button><button className={buttonClass} onClick={() => openEditor('reset', user)} aria-label={`Reset password for ${user.username}`}>Reset password</button></div></td>
              </tr>)}</tbody>
            </table></div>
            {!filtered.length && <p className="py-8 text-center text-gray-500">{users.length ? 'No users match your search.' : 'No POS users found. Create a user to get started.'}</p>}
          </>}
        </div>
      </div>
      {editor && <div className="fixed inset-0 z-[120] flex items-center justify-center overflow-y-auto bg-black/40 p-4">
        <section role="dialog" aria-modal="true" aria-labelledby="user-editor-title" className="max-h-[90dvh] w-full max-w-lg overflow-y-auto rounded-xl bg-white p-6 shadow-xl" onKeyDown={(event) => { if (event.key === 'Escape' && !saving) { setEditor(null); setForm({ ...emptyForm }); } }}>
          <h2 id="user-editor-title" className="text-xl font-bold">{editor.mode === 'create' ? 'Create POS user' : editor.mode === 'reset' ? `Reset password for ${editor.user.username}` : `Edit ${editor.user.username}`}</h2>
          <form onSubmit={save} className="mt-4 space-y-4">
            {formError && <p role="alert" className="rounded-lg bg-red-50 p-3 text-sm text-red-700">{formError}</p>}
            <fieldset disabled={saving} className="space-y-4">
              {editor.mode !== 'reset' && <>
                <label className="block text-sm font-medium">Username<input autoFocus required autoComplete="off" className={inputClass} value={form.username} onChange={(event) => change('username', event.target.value)} /></label>
                <fieldset disabled={ownAccount} className="rounded-lg border p-3">
                  <legend className="px-1 text-sm font-medium">Roles</legend>
                  <p className="mb-2 text-xs text-gray-500">Select one or more roles. The employee chooses their working role when signing in.</p>
                  <div className="grid max-h-48 grid-cols-2 gap-2 overflow-y-auto">{[...new Set([...ROLES, ...form.roles])].map(role => <label key={role} className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.roles.includes(role)} onChange={event => change('roles', event.target.checked ? [...form.roles, role] : form.roles.filter(value => value !== role))} />{role}</label>)}</div>
                  <p className="mt-2 text-xs text-gray-500">{form.roles.length} role(s) selected</p>
                </fieldset>
                <fieldset className="rounded-lg border p-3">
                  <legend className="px-1 text-sm font-medium">Location access{form.roles.includes('DIRECTOR') ? ' (optional)' : ''}</legend>
                  <p className="mb-2 text-xs text-gray-500">Select one or more locations. The user chooses their active location when signing in.</p>
                  <div className="grid max-h-48 grid-cols-2 gap-2 overflow-y-auto">
                    {asLocations([...locations, ...users.flatMap(user => asLocations(user.location)), ...form.location]).sort().map(location => (
                      <label key={location} className="flex items-center gap-2 text-sm">
                        <input type="checkbox" checked={form.location.includes(location)} onChange={event => change('location', event.target.checked ? [...form.location, location] : form.location.filter(value => value !== location))} />
                        {location}
                      </label>
                    ))}
                  </div>
                  <p className="mt-2 text-xs text-gray-500">{form.location.length} location(s) selected</p>
                </fieldset>
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" disabled={ownAccount} checked={form.isActive} onChange={(event) => change('isActive', event.target.checked)} />Active account</label>
                <p className="text-xs text-gray-500">{ownAccount ? 'Your own role and active status cannot be changed here.' : 'Inactive users cannot sign in.'}</p>
              </>}
              {editor.mode !== 'edit' && <>
                <label className="block text-sm font-medium">New password<input autoFocus={editor.mode === 'reset'} type="password" required minLength={8} autoComplete="new-password" className={inputClass} value={form.password} onChange={(event) => change('password', event.target.value)} /></label>
                <p className="text-xs text-gray-500">Use at least 8 characters.</p>
                <label className="block text-sm font-medium">Confirm password<input type="password" required minLength={8} autoComplete="new-password" className={inputClass} value={form.confirmPassword} onChange={(event) => change('confirmPassword', event.target.value)} /></label>
              </>}
            </fieldset>
            <div className="flex justify-end gap-2"><button type="button" disabled={saving} className={buttonClass} onClick={() => { setEditor(null); setForm({ ...emptyForm }); }}>Cancel</button><button type="submit" disabled={saving} className={`${buttonClass} bg-blue-700 text-white`}>{saving ? 'Saving…' : editor.mode === 'reset' ? 'Reset password' : editor.mode === 'create' ? 'Create user' : 'Save changes'}</button></div>
          </form>
        </section>
      </div>}
    </StockManagerLayout>
  );
}
